import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../context/AuthContext'
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts'
import {
  Bot,
  Phone,
  PhoneCall,
  PhoneForwarded,
  Percent,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  AlertTriangle,
  Server,
  Cpu,
  HardDrive,
  Database,
  Clock,
  UserCheck,
  UserX,
  Inbox,
  Plus,
  Minus,
  Activity,
  AlertCircle,
  XCircle,
  WifiOff,
  ArrowRight,
  RefreshCw,
  BarChart3,
  Crown,
  ChevronDown,
  ExternalLink,
  Mic,
  Tag,
  UserCircle,
  Cloud,
  FolderOpen,
  PhoneOff
} from 'lucide-react'
import {
  monitoringApi,
  clientAdminApi,
  financialsApi,
  backupApi,
  dialerApi,
  avatarApi,
  voiceSetApi,
  keywordApi,
  audiosocketBotsApi
} from '../services/api'
import MultiPicker from '../components/MultiPicker'
import './Dashboard.css'

// Client filter is remembered per browser.
const FILTER_KEY = 'dash-client-filter'
const loadFilter = () => { try { return JSON.parse(localStorage.getItem(FILTER_KEY)) || [] } catch (_) { return [] } }
const saveFilter = (v) => { try { localStorage.setItem(FILTER_KEY, JSON.stringify(v)) } catch (_) { } }

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  }
}

// Chart colors
const PIE_COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#6b7280', '#3b82f6', '#8b5cf6']
const BOT_STATUS_COLORS = {
  active: '#22c55e',    // Green - actively on calls
  waiting: '#f59e0b',   // Amber - logged out
  paused: '#3b82f6',    // Blue - paused
  offline: '#6b7280',   // Gray - offline
  crashed: '#ef4444'    // Red - crashed
}

// Custom tooltip for charts
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="dash-tooltip">
        <p className="dash-tooltip-label">{payload[0].name}</p>
        <p className="dash-tooltip-value">{payload[0].value.toLocaleString()}</p>
      </div>
    )
  }
  return null
}

const formatCompact = (n) => {
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return n.toLocaleString()
}

function Dashboard() {
  const { user, isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Data state
  const [fleetSummary, setFleetSummary] = useState(null) // real AudioSocket fleet counts
  const [machines, setMachines] = useState([]) // real AudioSocket machines (for client counts, same as Monitoring)
  const [botSummary, setBotSummary] = useState(null)
  const [dialersSummary, setDialersSummary] = useState(null)
  const [serversStats, setServersStats] = useState([])
  const [clients, setClients] = useState([])
  const [requests, setRequests] = useState([])
  const [financials, setFinancials] = useState(null)
  const [backupStats, setBackupStats] = useState(null)
  const [errors, setErrors] = useState([])
  const [dialers, setDialers] = useState([])
  const [inactiveBots, setInactiveBots] = useState([])
  const [requestStats, setRequestStats] = useState(null)
  const [avatars, setAvatars] = useState([])
  const [voiceSets, setVoiceSets] = useState([])
  const [globalCategories, setGlobalCategories] = useState([])
  const [keywordCategories, setKeywordCategories] = useState([])
  const [keywordSuggestions, setKeywordSuggestions] = useState({ high: [], medium: [], low: [] })
  // Calls this month (MTD) for the selected clients — /call-stats, same source as Monitoring.
  const [callStats, setCallStats] = useState(null)
  const [clientFilter, setClientFilter] = useState(loadFilter)   // client ids as strings, 'unassigned'; [] = all

  useEffect(() => {
    loadAllData()
  }, [])

  const loadCallStats = async () => {
    const now = new Date()
    const params = {
      from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
      to: now.toISOString().split('T')[0]
    }
    if (clientFilter.length) params.client_id = clientFilter.join(',')
    try {
      const r = await audiosocketBotsApi.callStats(params)
      setCallStats(r.data?.success ? r.data : null)
    } catch (e) {
      console.error('Failed to load call stats:', e)
    }
  }
  useEffect(() => {
    saveFilter(clientFilter)
    loadCallStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter.join(',')])

  // Helper to get bot count from request - checks bot_count, bot_ids, or sips range
  const getBotCountFromRequest = (request) => {
    // First check bot_count
    if (request.bot_count) return request.bot_count

    // Check bot_ids array
    if (request.bot_ids) {
      try {
        const ids = typeof request.bot_ids === 'string'
          ? JSON.parse(request.bot_ids)
          : request.bot_ids
        if (Array.isArray(ids) && ids.length > 0) return ids.length
      } catch (e) {
        // ignore parse error
      }
    }

    // Check sips range (e.g., "1001-1010" = 10 bots)
    if (request.sips) {
      const rangeMatch = request.sips.match(/^(\d+)-(\d+)$/)
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10)
        const end = parseInt(rangeMatch[2], 10)
        return end - start + 1
      }
    }

    return 1 // default
  }

  const loadAllData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    const now = new Date()
    if (isRefresh) loadCallStats()

    try {
      const results = await Promise.allSettled([
        monitoringApi.getSummary(),
        Promise.resolve(null), // (was MTD stats overview — now callStats)
        monitoringApi.getDialersSummary(),
        monitoringApi.getServersStats(),
        clientAdminApi.getClients(),
        financialsApi.getRequests('pending'), // Use financials API like Request Inbox page
        financialsApi.getDashboard(now.getMonth() + 1, now.getFullYear()),
        backupApi.getStats(),
        monitoringApi.getErrors({ limit: 10, resolved: false }),
        dialerApi.getAll(),
        monitoringApi.getInactiveBots(3), // Bots inactive for 3+ days
        financialsApi.getRequestStats(), // Request stats from financials API
        avatarApi.getAll(), // Avatars
        voiceSetApi.getAll(), // Voice sets
        voiceSetApi.getGlobalCategories(), // Global categories - returns { success: true, categories: [...] }
        keywordApi.getAll(), // Keyword categories with keywords array (same as KeywordCategories page)
        keywordApi.getSuggestions(7, 10), // Suggestions (same as KeywordCategories page)
        Promise.resolve(null), // (was MTD stats by dialer — now callStats)
        monitoringApi.getFleetSummary(), // real AudioSocket fleet counts (bots/servers online)
        audiosocketBotsApi.listMachines() // real machines (client counts, same source as Monitoring)
      ])

      if (results[0].status === 'fulfilled') setBotSummary(results[0].value.data)
      if (results[2].status === 'fulfilled') setDialersSummary(results[2].value.data)
      if (results[3].status === 'fulfilled') setServersStats(results[3].value.data?.servers || [])
      if (results[4].status === 'fulfilled') setClients(results[4].value.data?.clients || [])
      if (results[5].status === 'fulfilled') setRequests(results[5].value.data?.requests || [])
      if (results[6].status === 'fulfilled') setFinancials(results[6].value.data)
      if (results[7].status === 'fulfilled') setBackupStats(results[7].value.data?.data?.summary || results[7].value.data?.summary || results[7].value.data)
      if (results[8].status === 'fulfilled') setErrors(results[8].value.data?.errors || [])
      if (results[9].status === 'fulfilled') setDialers(Array.isArray(results[9].value.data) ? results[9].value.data : [])
      if (results[10].status === 'fulfilled') setInactiveBots(results[10].value.data?.bots || [])
      if (results[11].status === 'fulfilled') setRequestStats(results[11].value.data?.stats || null)
      if (results[12].status === 'fulfilled') setAvatars(Array.isArray(results[12].value.data) ? results[12].value.data : [])
      if (results[13].status === 'fulfilled') setVoiceSets(Array.isArray(results[13].value.data) ? results[13].value.data : [])
      if (results[14].status === 'fulfilled') setGlobalCategories(results[14].value.data?.categories || [])
      if (results[15].status === 'fulfilled') setKeywordCategories(Array.isArray(results[15].value.data) ? results[15].value.data : [])
      if (results[16].status === 'fulfilled') setKeywordSuggestions(results[16].value.data?.suggestions || { high: [], medium: [], low: [] })
      if (results[18].status === 'fulfilled') setFleetSummary(results[18].value.data)
      if (results[19].status === 'fulfilled') setMachines(results[19].value.data?.machines || [])
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Client filter scope. Bot / call / client numbers below follow it; system
  // stats (avatars, voice sets, backups…) are global.
  const filtering = clientFilter.length > 0
  const inScope = (clientId) => !filtering || clientFilter.includes(clientId == null ? 'unassigned' : String(clientId))
  const scopedMachines = useMemo(() => (machines || []).filter(m => inScope(m.client_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [machines, clientFilter.join(',')])
  const scopedSips = useMemo(() => new Set(scopedMachines.map(m => String(m.sip_user))), [scopedMachines])
  const clientOptions = useMemo(() => {
    const counts = new Map()
    for (const m of machines || []) {
      const k = m.client_id == null ? 'unassigned' : String(m.client_id)
      counts.set(k, (counts.get(k) || 0) + 1)
    }
    const name = (k) => k === 'unassigned' ? 'Unassigned' : (clients.find(c => String(c.id) === k)?.company || clients.find(c => String(c.id) === k)?.name || `Client #${k}`)
    return [...counts].map(([k, n]) => ({ value: k, label: name(k), sub: `${n} bot${n === 1 ? '' : 's'}` }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [machines, clients])
  const monitoringLink = clientFilter.length === 1
    ? `/monitoring-v3?client=${clientFilter[0] === 'unassigned' ? 'unassigned' : clientFilter[0]}`
    : '/monitoring-v3'

  // Computed KPIs
  const kpis = useMemo(() => {
    // Bot Activity — use the REAL AudioSocket fleet (audiosocket_machines), not the
    // dead legacy dialers/bot_status tables. online = had a call in the last 15 min.
    // With a client filter, count the filtered machines (online = status 'active', as fleet-summary does).
    const botsOnline = filtering ? scopedMachines.filter(m => m.status === 'active').length : (fleetSummary?.bots?.online ?? 0)
    const totalBots = filtering ? scopedMachines.length : (fleetSummary?.bots?.total ?? 0)
    const botsOffline = Math.max(0, totalBots - botsOnline)

    // MTD calls for the filtered clients
    const mtdCalls = callStats?.totals?.total || 0
    const mtdTransfers = callStats?.totals?.transfers || 0

    const dc = callStats?.disposition_counts || {}
    const answeringMachines = dc['answering-machine'] || 0
    const doNotCall = dc['do-not-call'] || 0
    const notInterested = dc['not-interested'] || 0
    const noAnswers = dc['no-answer'] || 0

    // System Health — real AudioSocket servers (audiosocket_servers). online = a server
    // with call activity in the last 15 min. Falls back to legacy serversStats for VM/CPU.
    const serversOnline = filtering
      ? new Set(scopedMachines.filter(m => m.status === 'active').map(m => m.server_id)).size
      : (fleetSummary?.servers?.online ?? serversStats.filter(s => s.online).length)
    const totalServers = filtering
      ? new Set(scopedMachines.map(m => m.server_id).filter(Boolean)).size
      : (fleetSummary?.servers?.total ?? serversStats.length)
    const vmsRunning = serversStats.reduce((sum, s) => sum + (s.vm_running || 0), 0)
    const totalVms = serversStats.reduce((sum, s) => sum + (s.vm_count || 0), 0)
    const avgCpu = serversStats.length > 0
      ? (serversStats.reduce((sum, s) => sum + (s.cpu_usage || 0), 0) / serversStats.length).toFixed(0)
      : 0

    // Backup stats - API returns { summary: { lastBackup: { date, size, database } } }
    const lastBackupDate = backupStats?.lastBackup?.date
    let lastBackup = 'N/A'
    if (lastBackupDate) {
      const backupTime = new Date(lastBackupDate)
      const hoursAgo = Math.floor((Date.now() - backupTime.getTime()) / (1000 * 60 * 60))
      if (hoursAgo < 24) {
        lastBackup = `${hoursAgo}h ago`
      } else {
        lastBackup = backupTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    }

    // Clients — counted from the real machines list, EXACTLY like the Monitoring page:
    // total = distinct clients that have bots (+ an "Unassigned" group if any bot has no
    // client); active = distinct clients that have at least one ACTIVE bot.
    const mList = scopedMachines
    const clientsWithBots = new Set(mList.filter(m => m.client_id != null).map(m => m.client_id))
    const hasUnassigned = mList.some(m => m.client_id == null)
    const totalClients = clientsWithBots.size + (hasUnassigned ? 1 : 0)
    const activeClients = new Set(mList.filter(m => m.client_id != null && m.status === 'active').map(m => m.client_id)).size
    const scopedClients = clients.filter(c => inScope(c.id))
    const pausedClients = scopedClients.filter(c => c.status === 'paused').length
    const clientBots = scopedClients.reduce((sum, c) => sum + (c.total_bots || 0), 0)
    const needingAttention = scopedClients.filter(c =>
      (parseFloat(c.balance) < 100 && c.status === 'active') ||
      c.status === 'suspended'
    ).length

    // Requests - use requestStats from financials API (same as Request Inbox page)
    const pendingRequests = requestStats?.pending || 0
    const inProgressRequests = requestStats?.in_progress || 0
    // Bot additions/removals should be TOTAL NUMBER OF BOTS, not number of requests
    // Use helper that checks bot_count, bot_ids, and sips range
    const botAdditions = requests
      .filter(r => r.type === 'bot_addition')
      .reduce((sum, r) => sum + getBotCountFromRequest(r), 0)
    const botRemovals = requests
      .filter(r => r.type === 'bot_removal')
      .reduce((sum, r) => sum + getBotCountFromRequest(r), 0)

    return {
      botActivity: { botsOnline, totalBots, botsOffline, mtdCalls, mtdTransfers },
      dispositions: { answeringMachines, doNotCall, notInterested, noAnswers },
      systemHealth: { serversOnline, totalServers, vmsRunning, totalVms, avgCpu, lastBackup },
      clients: { active: activeClients, total: totalClients, paused: pausedClients, totalBots: clientBots, needingAttention },
      requests: { pending: pendingRequests, inProgress: inProgressRequests, additions: botAdditions, removals: botRemovals }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botSummary, callStats, dialersSummary, serversStats, clients, requests, financials, backupStats, requestStats, fleetSummary, scopedMachines, filtering])

  // System Stats - Avatars, Voice Sets, Keywords, Backups (same as dedicated pages)
  const systemStats = useMemo(() => {
    // Avatars - total and archived (same as Avatars.jsx)
    const totalAvatars = avatars.filter(a => !a.archived).length
    const archivedAvatars = avatars.filter(a => a.archived).length

    // Voice Sets - total and global categories (same as VoiceSets.jsx)
    const totalVoiceSets = voiceSets.length
    const totalGlobalCategories = globalCategories.length

    // Keywords - computed from categories like KeywordCategories.jsx
    // totalKeywords = categories.reduce((sum, cat) => sum + (cat.keywords?.length || 0), 0)
    const totalKeywords = keywordCategories.reduce((sum, cat) => sum + (cat.keywords?.length || 0), 0)
    // Suggestions count from getSuggestions API (high priority count)
    const suggestedKeywords = (keywordSuggestions?.high?.length || 0) +
                              (keywordSuggestions?.medium?.length || 0) +
                              (keywordSuggestions?.low?.length || 0)

    // Backups - daily and monthly counts
    const dailyBackups = backupStats?.totalDailyBackups || 0
    const monthlyBackups = backupStats?.totalMonthlyBackups || 0

    return {
      avatars: { total: totalAvatars, archived: archivedAvatars },
      voiceSets: { total: totalVoiceSets, categories: totalGlobalCategories },
      keywords: { total: totalKeywords, suggested: suggestedKeywords },
      backups: { daily: dailyBackups, monthly: monthlyBackups }
    }
  }, [avatars, voiceSets, globalCategories, keywordCategories, keywordSuggestions, backupStats])

  // Actionable Lists - keep full arrays for counts, display limited in UI
  const actionableLists = useMemo(() => {
    // Pending bot additions/removals (from financialsApi - same as Request Inbox page)
    // Field is 'type' with values 'bot_addition' / 'bot_removal'
    const pendingAdditions = requests
      .filter(r => r.type === 'bot_addition' && r.status === 'pending')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    const pendingRemovals = requests
      .filter(r => r.type === 'bot_removal' && r.status === 'pending')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Servers - use vm_running and vm_count, calculate memory % from ram_used_gb/ram_total_gb
    const lowUtilizedAll = serversStats
      .filter(s => s.vm_count > 0 && s.vm_running < (s.vm_count * 0.3))

    const overCapacityAll = serversStats
      .filter(s => {
        const memPct = s.ram_total_gb > 0 ? (s.ram_used_gb / s.ram_total_gb) * 100 : 0
        return s.cpu_usage > 80 || memPct > 85
      })

    // Clients - same as Clients page (balance <= 50 for low balance)
    const lowBalanceAll = clients
      .filter(c => inScope(c.id) && parseFloat(c.balance) <= 50 && c.status === 'active')
      .sort((a, b) => parseFloat(a.balance) - parseFloat(b.balance))

    const inactiveClientsAll = clients
      .filter(c => inScope(c.id) && (c.status === 'paused' || c.status === 'suspended'))

    // Bots (from dialers summary for crashed, from inactiveBots API for 3+ days offline)
    const sipInScope = (b) => !filtering || scopedSips.has(String(b.sip_id ?? b.sip_user ?? b.name))
    const crashedBotsAll = (dialersSummary?.issues?.crashed || []).filter(sipInScope)
    const offlineBotsAll = (inactiveBots || []).filter(sipInScope)

    // Dialers performance - from monitoringApi.getStatsByDialer (same as Stats page)
    // Uses transfer_rate, total_calls, transfers fields
    const rankRows = callStats?.by_client || []
    const topPerformersAll = rankRows
      .filter(d => d.total > 10)
      .sort((a, b) => (b.transfer_rate || 0) - (a.transfer_rate || 0))

    const underPerformersAll = rankRows
      .filter(d => d.total > 10 && (d.transfer_rate || 0) < 5)
      .sort((a, b) => (a.transfer_rate || 0) - (b.transfer_rate || 0))

    const recentErrorsAll = (errors || []).filter(e => !filtering || scopedSips.has(String(e.sip_id)))

    return {
      requests: {
        added: pendingAdditions.slice(0, 3),
        addedTotal: pendingAdditions.length,
        removed: pendingRemovals.slice(0, 3),
        removedTotal: pendingRemovals.length
      },
      servers: {
        lowUtilized: lowUtilizedAll.slice(0, 3),
        lowUtilizedTotal: lowUtilizedAll.length,
        overCapacity: overCapacityAll.slice(0, 3),
        overCapacityTotal: overCapacityAll.length
      },
      clients: {
        lowBalance: lowBalanceAll.slice(0, 3),
        lowBalanceTotal: lowBalanceAll.length,
        inactive: inactiveClientsAll.slice(0, 3),
        inactiveTotal: inactiveClientsAll.length
      },
      bots: {
        crashed: crashedBotsAll.slice(0, 3),
        crashedTotal: crashedBotsAll.length,
        offline: offlineBotsAll.slice(0, 3),
        offlineTotal: offlineBotsAll.length
      },
      dialers: {
        topPerformers: topPerformersAll.slice(0, 3),
        topPerformersTotal: topPerformersAll.length,
        underPerformers: underPerformersAll.slice(0, 3),
        underPerformersTotal: underPerformersAll.length
      },
      errors: {
        recent: recentErrorsAll.slice(0, 3),
        recentTotal: recentErrorsAll.length
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, serversStats, clients, dialersSummary, callStats, inactiveBots, errors, scopedSips, filtering])

  // Rankings: clients, or that client's SIPs when exactly one client is picked.
  const bySip = clientFilter.length === 1
  const rankingRows = useMemo(() => {
    if (!callStats) return []
    if (bySip) return (callStats.by_sip || []).filter(r => r.sip_user)
      .map(r => ({ key: r.sip_user, name: `SIP ${r.sip_user}`, total: r.total, transfers: r.transfers, transfer_rate: r.transfer_rate, link: `${monitoringLink}&sip=${r.sip_user}` }))
    return (callStats.by_client || []).map(r => ({
      key: r.client_id ?? 'none',
      name: r.client_name || (r.client_id ? `Client #${r.client_id}` : 'Unassigned'),
      total: r.total, transfers: r.transfers, transfer_rate: r.transfer_rate,
      link: `/monitoring-v3?client=${r.client_id ?? 'unassigned'}`
    }))
  }, [callStats, bySip, monitoringLink])

  // Chart data preparation
  const chartData = useMemo(() => {
    // Disposition pie chart data
    const dispositionData = []
    const dc = callStats?.disposition_counts || {}
    if (dc['transfer-call']) dispositionData.push({ name: 'Transfers', value: dc['transfer-call'], color: '#22c55e' })
    if (dc['answering-machine']) dispositionData.push({ name: 'Ans. Machine', value: dc['answering-machine'], color: '#f59e0b' })
    if (dc['not-interested']) dispositionData.push({ name: 'Not Interested', value: dc['not-interested'], color: '#ef4444' })
    if (dc['do-not-call']) dispositionData.push({ name: 'DNC', value: dc['do-not-call'], color: '#8b5cf6' })
    if (dc['no-answer']) dispositionData.push({ name: 'No Answer', value: dc['no-answer'], color: '#6b7280' })

    // Bot status donut — real AudioSocket fleet (online vs offline), or the
    // filtered clients' bots.
    const totalBots = kpis.botActivity.totalBots
    const onlineCount = kpis.botActivity.botsOnline
    const offlineCount = Math.max(0, totalBots - onlineCount)

    const botStatusData = [
      { name: 'Online', value: onlineCount, color: BOT_STATUS_COLORS.active },
      { name: 'Offline', value: offlineCount, color: BOT_STATUS_COLORS.offline }
    ].filter(d => d.value > 0)

    // Performance bars (top 6): per client, or per SIP when one client is picked
    const dialerBarData = rankingRows.slice(0, 6).map(d => ({
      name: d.name.substring(0, 15),
      calls: d.total,
      transfers: d.transfers,
      rate: d.transfer_rate
    }))

    return { dispositionData, botStatusData, dialerBarData }
  }, [callStats, kpis, rankingRows])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toLocaleString()
  }

  return (
    <motion.div
      className="dashboard-modern"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="dash-header" variants={itemVariants}>
        <div className="dash-header-left">
          <h1><BarChart3 size={28} /> Dashboard</h1>
        </div>
        <div className="dash-header-right">
          <MultiPicker label="Clients" icon={Users} options={clientOptions} selected={clientFilter}
            onChange={setClientFilter} allLabel="All clients" align="right" />
          <div className="dash-date-badge">
            <Clock size={14} />
            {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <button className="dash-refresh-btn" onClick={() => loadAllData(true)} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </motion.div>

      {/* KPI Stat Blocks */}
      <motion.div className="stat-blocks" variants={itemVariants}>
        <Link to={monitoringLink} className="stat-block blue">
          <div className="stat-icon"><Bot size={18} /></div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '...' : `${kpis.botActivity.botsOnline}/${kpis.botActivity.totalBots}`}</span>
            <span className="stat-label">Bots Online</span>
          </div>
        </Link>
        <Link to={monitoringLink} className="stat-block green">
          <div className="stat-icon"><PhoneForwarded size={18} /></div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '...' : formatNumber(kpis.botActivity.mtdTransfers)}</span>
            <span className="stat-label">MTD Transfers</span>
          </div>
        </Link>
        <Link to={monitoringLink} className="stat-block orange">
          <div className="stat-icon"><Phone size={18} /></div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '...' : formatNumber(kpis.botActivity.mtdCalls)}</span>
            <span className="stat-label">MTD Calls</span>
          </div>
        </Link>
        {isAdmin && (
          <Link to="/servers" className="stat-block purple">
            <div className="stat-icon"><Server size={18} /></div>
            <div className="stat-content">
              <span className="stat-value">{loading ? '...' : `${kpis.systemHealth.serversOnline}/${kpis.systemHealth.totalServers}`}</span>
              <span className="stat-label">Servers Online</span>
            </div>
          </Link>
        )}
        {isAdmin && (
          <Link to="/clients" className="stat-block cyan">
            <div className="stat-icon"><Users size={18} /></div>
            <div className="stat-content">
              <span className="stat-value">{loading ? '...' : `${kpis.clients.active}/${kpis.clients.total}`}</span>
              <span className="stat-label">Active Clients</span>
            </div>
          </Link>
        )}
      </motion.div>

      {/* Charts Row */}
      <motion.div className="dash-charts-row" variants={itemVariants}>
        {/* Disposition Pie Chart */}
        <div className="dash-chart-card">
          <h3 className="dash-chart-title">Call Dispositions</h3>
          {chartData.dispositionData.length > 0 ? (
            <div className="dash-chart-with-legend">
              <div className="dash-pie-container">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.dispositionData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                      {chartData.dispositionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="dash-chart-legend">
                {chartData.dispositionData.map((item, i) => (
                  <div key={i} className="dash-legend-item">
                    <span className="dash-legend-color" style={{ background: item.color }}></span>
                    <span className="dash-legend-label">{item.name}</span>
                    <span className="dash-legend-value">{formatCompact(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dash-chart-empty">No disposition data</div>
          )}
        </div>

        {/* Bot Status Donut */}
        <div className="dash-chart-card">
          <h3 className="dash-chart-title">Bot Status</h3>
          {chartData.botStatusData.length > 0 ? (
            <div className="dash-chart-with-legend">
              <div className="dash-pie-container">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={chartData.botStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                      {chartData.botStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="dash-donut-center">
                  <span className="dash-donut-value">{kpis.botActivity.totalBots}</span>
                  <span className="dash-donut-label">Total</span>
                </div>
              </div>
              <div className="dash-chart-legend">
                {chartData.botStatusData.map((item, i) => (
                  <div key={i} className="dash-legend-item">
                    <span className="dash-legend-color" style={{ background: item.color }}></span>
                    <span className="dash-legend-label">{item.name}</span>
                    <span className="dash-legend-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="dash-chart-empty">No bot data</div>
          )}
        </div>

        {/* Dialer Performance Bar */}
        <div className="dash-chart-card wide">
          <h3 className="dash-chart-title">{bySip ? 'SIP' : 'Client'} Performance (MTD)</h3>
          {chartData.dialerBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData.dialerBarData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px' }} />
                <Bar dataKey="calls" fill="var(--text-muted)" name="Calls" radius={[0, 4, 4, 0]} />
                <Bar dataKey="transfers" fill="#22c55e" name="Transfers" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="dash-chart-empty">No client data</div>
          )}
        </div>
      </motion.div>

      {/* Stats-Style Panels Row */}
      <motion.div className="dash-panels-row" variants={itemVariants}>
        {/* Dialer Rankings Panel */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3><Phone size={16} /> {bySip ? 'SIP' : 'Client'} Rankings</h3>
            <Link to={monitoringLink} className="dash-panel-action">View Stats <ExternalLink size={12} /></Link>
          </div>
          <div className="dash-panel-list">
            {loading ? (
              <div className="dash-panel-empty">Loading...</div>
            ) : rankingRows.length > 0 ? (
              rankingRows.slice(0, 5).map((dialer, i) => {
                const maxCalls = rankingRows[0]?.total || 1
                const barWidth = (dialer.total / maxCalls) * 100
                return (
                  <Link key={dialer.key} to={dialer.link} className={`dash-panel-item ${i === 0 ? 'top' : ''}`}>
                    <div className="dash-panel-item-bar" style={{ width: `${barWidth}%` }}></div>
                    <div className="dash-panel-item-content">
                      <span className="dash-panel-rank">{i + 1}</span>
                      <span className="dash-panel-name">{dialer.name}</span>
                      <div className="dash-panel-metrics">
                        <span className="dash-metric">{dialer.total?.toLocaleString()}<small>calls</small></span>
                        <span className={`dash-metric-rate ${dialer.transfer_rate >= 5 ? 'good' : dialer.transfer_rate >= 2 ? 'mid' : 'low'}`}>
                          {(dialer.transfer_rate || 0).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </Link>
                )
              })
            ) : (
              <div className="dash-panel-empty">No client data</div>
            )}
          </div>
        </div>

        {/* Client Alerts Panel */}
        {isAdmin && (
          <div className="dash-panel">
            <div className="dash-panel-header">
              <h3><Users size={16} /> Client Alerts</h3>
              <Link to="/clients" className="dash-panel-action">View All <ExternalLink size={12} /></Link>
            </div>
            <div className="dash-panel-list">
              {loading ? (
                <div className="dash-panel-empty">Loading...</div>
              ) : (actionableLists.clients.lowBalance.length > 0 || actionableLists.clients.inactive.length > 0) ? (
                <>
                  {actionableLists.clients.lowBalance.slice(0, 3).map((client, i) => (
                    <Link key={client.id} to={`/client-details/${client.id}`} className="dash-panel-item alert">
                      <div className="dash-panel-item-content">
                        <span className="dash-panel-icon red"><Wallet size={14} /></span>
                        <span className="dash-panel-name">{client.company_name || client.name}</span>
                        <span className="dash-metric-rate low">{formatCurrency(client.balance)}</span>
                      </div>
                    </Link>
                  ))}
                  {actionableLists.clients.inactive.slice(0, 2).map((client, i) => (
                    <Link key={client.id} to={`/client-details/${client.id}`} className="dash-panel-item">
                      <div className="dash-panel-item-content">
                        <span className="dash-panel-icon orange"><UserX size={14} /></span>
                        <span className="dash-panel-name">{client.company_name || client.name}</span>
                        <span className="dash-badge">{client.status}</span>
                      </div>
                    </Link>
                  ))}
                </>
              ) : (
                <div className="dash-panel-empty">All clients healthy</div>
              )}
            </div>
          </div>
        )}

        {/* Bot Issues Panel */}
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3><Bot size={16} /> Bot Issues</h3>
            <Link to={monitoringLink} className="dash-panel-action">View All <ExternalLink size={12} /></Link>
          </div>
          <div className="dash-panel-list">
            {loading ? (
              <div className="dash-panel-empty">Loading...</div>
            ) : (actionableLists.bots.crashed.length > 0 || actionableLists.bots.offline.length > 0) ? (
              <>
                {actionableLists.bots.crashed.slice(0, 3).map((bot, i) => (
                  <div key={i} className="dash-panel-item alert">
                    <div className="dash-panel-item-content">
                      <span className="dash-panel-icon red"><XCircle size={14} /></span>
                      <span className="dash-panel-name">{bot.sip_id || bot.name}</span>
                      <span className="dash-badge red">Crashed</span>
                    </div>
                  </div>
                ))}
                {actionableLists.bots.offline.slice(0, 2).map((bot, i) => (
                  <div key={i} className="dash-panel-item">
                    <div className="dash-panel-item-content">
                      <span className="dash-panel-icon orange"><WifiOff size={14} /></span>
                      <span className="dash-panel-name">{bot.sip_id || bot.name}</span>
                      <span className="dash-badge">{bot.days_inactive}d offline</span>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="dash-panel-empty success">All bots operational</div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Bottom Row: Requests + System Stats */}
      <motion.div className="dash-bottom-row" variants={itemVariants}>
        {/* System Stats Grid */}
        {isAdmin && (
          <div className="dash-system-stats">
            <Link to="/avatars" className="dash-mini-stat cyan">
              <UserCircle size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.avatars.total}</span>
                <span className="dash-mini-stat-label">Avatars</span>
              </div>
            </Link>
            <Link to="/voice-sets" className="dash-mini-stat purple">
              <Mic size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.voiceSets.total}</span>
                <span className="dash-mini-stat-label">Voice Sets</span>
              </div>
            </Link>
            <Link to="/keywords" className="dash-mini-stat green">
              <Tag size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.keywords.total}</span>
                <span className="dash-mini-stat-label">Keywords</span>
              </div>
            </Link>
            <div className="dash-mini-stat blue">
              <Cloud size={20} />
              <div className="dash-mini-stat-content">
                <span className="dash-mini-stat-value">{loading ? '...' : systemStats.backups.daily}</span>
                <span className="dash-mini-stat-label">Backups</span>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Recent Errors (only if errors exist) */}
      {isAdmin && actionableLists.errors.recentTotal > 0 && (
        <motion.div className="dash-errors-section" variants={itemVariants}>
          <div className="dash-panel-header">
            <h3><AlertCircle size={16} /> Recent Errors ({actionableLists.errors.recentTotal})</h3>
            <Link to="/monitoring?tab=errors" className="dash-panel-action">View All <ExternalLink size={12} /></Link>
          </div>
          <div className="dash-errors-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Bot</th>
                  <th>Error</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {actionableLists.errors.recent.map((error, i) => (
                  <tr key={i}>
                    <td>{new Date(error.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{error.sip_id || 'Unknown'}</td>
                    <td>{error.message || error.error_type}</td>
                    <td><span className={`dash-error-status ${error.resolved ? 'resolved' : ''}`}>{error.resolved ? 'Resolved' : 'Open'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}

export default Dashboard
